import type {
  Product,
  Inquiry,
  InquiryFormData,
  PaginatedResponse,
  Category,
  Admin,
  ProductFilters,
  FeaturedCategoryItem,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

type ProductQueryParams = Partial<ProductFilters> & {
  includeInactive?: boolean;
};

function buildProductQuery(params?: ProductQueryParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (!params) {
    return searchParams;
  }

  if (params.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params.categorySlug) searchParams.set('categorySlug', params.categorySlug);
  if (params.productType) searchParams.set('productType', params.productType);
  if (params.search) searchParams.set('search', params.search);
  if (params.featured !== undefined) searchParams.set('featured', String(params.featured));
  if (params.includeInactive !== undefined) {
    searchParams.set('includeInactive', String(params.includeInactive));
  }
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));

  return searchParams;
}

/**
 * Auth Types
 */
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  admin: Admin;
}

/**
 * Get token from localStorage
 */
function getToken(): string {
  const storedAuth = localStorage.getItem('turco-admin-auth');
  if (storedAuth) {
    try {
      const parsed = JSON.parse(storedAuth);
      return parsed.state?.token || '';
    } catch {
      return '';
    }
  }
  return '';
}

/**
 * Get auth headers from localStorage
 */
export function getAuthHeaders(): HeadersInit {
  const storedAuth = localStorage.getItem('turco-admin-auth');
  if (storedAuth) {
    try {
      const parsed = JSON.parse(storedAuth);
      const token = parsed.state?.token;
      if (token) {
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };
      }
    } catch (e) {
      console.warn('Auth token parse error:', e);
    }
  }
  return { 'Content-Type': 'application/json' };
}

/**
 * Handle 401 Unauthorized - clear auth and redirect to login
 */
function handleUnauthorized() {
  // Clear localStorage
  localStorage.removeItem('turco-admin-auth');
  // Redirect to login if in admin area
  if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
    window.location.href = '/admin/login';
  }
}

/**
 * Generic fetch wrapper with error handling
 * Extracts data from the wrapped response { success, data, timestamp }
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  const json = await response.json();

  // Backend wraps responses in { success, data, timestamp }
  // Return the data field if it exists, otherwise return the whole response
  if (json && typeof json === 'object' && 'data' in json && 'success' in json) {
    return json.data as T;
  }

  return json as T;
}

/**
 * Category API endpoints
 */
export const categoriesApi = {
  // Admin: Get all categories (including hidden)
  getAll: async (): Promise<Category[]> => {
    const response = await fetch(`${API_BASE_URL}/categories/all`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      throw new Error('Kategoriler yuklenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Public: Get visible categories only
  getVisible: (): Promise<Category[]> => {
    return fetchApi('/categories/visible');
  },

  getBySlug: (slug: string, includeProducts = false): Promise<Category> => {
    return fetchApi(`/categories/slug/${slug}${includeProducts ? '?includeProducts=true' : ''}`);
  },

  getById: (id: string): Promise<Category> => {
    return fetchApi(`/categories/${id}`);
  },

  // Admin CRUD operations
  create: async (data: FormData): Promise<Category> => {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: data, // FormData (multipart)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Kategori oluşturulamadı' }));
      throw new Error(error.message || 'Kategori oluşturulamadı');
    }
    const json = await response.json();
    return json.data || json;
  },

  update: async (id: string, data: FormData): Promise<Category> => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: data,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Kategori güncellenemedi' }));
      throw new Error(error.message || 'Kategori güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  delete: async (id: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Kategori silinemedi' }));
      throw new Error(error.message || 'Kategori silinemedi');
    }
    return true;
  },

  bulkDelete: async (ids: string[]): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/categories/bulk-delete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Kategoriler silinemedi' }));
      throw new Error(error.message || 'Kategoriler silinemedi');
    }
    return true;
  },

  updateVisibility: async (id: string, hidden: boolean): Promise<Category> => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}/visibility`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ hidden }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Görünürlük güncellenemedi' }));
      throw new Error(error.message || 'Görünürlük güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  reorder: async (id: string, newPosition: number): Promise<Category> => {
    const response = await fetch(`${API_BASE_URL}/categories/${id}/reorder`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newPosition }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Sıralama güncellenemedi' }));
      throw new Error(error.message || 'Sıralama güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },
};

/**
 * Product API endpoints
 */
export const productsApi = {
  // Admin: Get paginated products (optionally including hidden)
  getAdminList: async (
    params?: ProductQueryParams,
  ): Promise<PaginatedResponse<Product>> => {
    const query = buildProductQuery(params);
    const endpoint = `${API_BASE_URL}/products${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await fetch(endpoint, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Urunler yuklenemedi' }));
      throw new Error(error.message || 'Urunler yuklenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Public: Get visible products with optional filters
  getVisible: async (
    params?: ProductFilters,
  ): Promise<PaginatedResponse<Product>> => {
    const query = buildProductQuery(params);
    const queryString = query.toString();
    return fetchApi(`/products/visible${queryString ? `?${queryString}` : ''}`);
  },

  getBySlug: (slug: string): Promise<Product> => {
    return fetchApi(`/products/slug/${slug}`);
  },

  getRelated: (productId: string, limit = 3): Promise<Product[]> => {
    return fetchApi(`/products/${productId}/similar?limit=${limit}`);
  },

  // Get single product by ID (for admin edit modal)
  getById: async (id: string): Promise<Product> => {
    // Add timestamp to prevent caching
    const response = await fetch(`${API_BASE_URL}/products/${id}?t=${Date.now()}`, {
      headers: {
        ...getAuthHeaders() as Record<string, string>,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
    });
    if (!response.ok) {
      throw new Error('Ürün bulunamadı');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Admin CRUD operations
  create: async (data: FormData): Promise<Product> => {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: data,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ürün oluşturulamadı' }));
      throw new Error(error.message || 'Ürün oluşturulamadı');
    }
    const json = await response.json();
    return json.data || json;
  },

  update: async (id: string, data: FormData): Promise<Product> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: data,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ürün güncellenemedi' }));
      throw new Error(error.message || 'Ürün güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  delete: async (id: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ürün silinemedi' }));
      throw new Error(error.message || 'Ürün silinemedi');
    }
    return true;
  },

  bulkDelete: async (ids: string[]): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/products/bulk-delete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ürünler silinemedi' }));
      throw new Error(error.message || 'Ürünler silinemedi');
    }
    return true;
  },

  updateVisibility: async (id: string, hidden: boolean): Promise<Product> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}/visibility`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ hidden }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Görünürlük güncellenemedi' }));
      throw new Error(error.message || 'Görünürlük güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  addSimilar: async (id: string, similarProductId: string): Promise<{ message: string }> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}/similar`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ similarProductId }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Benzer ürün eklenemedi' }));
      throw new Error(error.message || 'Benzer ürün eklenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  removeSimilar: async (id: string, similarId: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}/similar/${similarId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Benzer ürün kaldırılamadı' }));
      throw new Error(error.message || 'Benzer ürün kaldırılamadı');
    }
    return true;
  },

  reorder: async (id: string, newPosition: number): Promise<Product> => {
    const response = await fetch(`${API_BASE_URL}/products/${id}/order`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newPosition }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Sıralama güncellenemedi' }));
      throw new Error(error.message || 'Sıralama güncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  uploadColorImage: async (file: File): Promise<{ success: boolean; image: { cloudflareId: string; url: string; filename: string } }> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/products/upload/color-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Görsel yüklenemedi' }));
      throw new Error(error.message || 'Görsel yüklenemedi');
    }

    return response.json();
  },
};

/**
 * Inquiry API endpoints
 */
export const inquiriesApi = {
  create: (data: InquiryFormData): Promise<Inquiry> => {
    return fetchApi('/inquiries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin operations
  getAll: async (params?: { status?: string; page?: number; limit?: number }): Promise<PaginatedResponse<Inquiry>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/inquiries${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu. Lutfen tekrar giris yapin.');
      }
      const error = await response.json().catch(() => ({ message: 'Talepler yuklenemedi' }));
      throw new Error(error.message || 'Talepler yuklenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  getOne: async (id: string): Promise<Inquiry> => {
    const response = await fetch(`${API_BASE_URL}/inquiries/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu. Lutfen tekrar giris yapin.');
      }
      const error = await response.json().catch(() => ({ message: 'Talep bulunamadi' }));
      throw new Error(error.message || 'Talep bulunamadi');
    }
    const json = await response.json();
    return json.data || json;
  },

  update: async (id: string, data: { status?: string; notes?: string }): Promise<Inquiry> => {
    const response = await fetch(`${API_BASE_URL}/inquiries/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu. Lutfen tekrar giris yapin.');
      }
      const error = await response.json().catch(() => ({ message: 'Talep guncellenemedi' }));
      throw new Error(error.message || 'Talep guncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  getStats: async (): Promise<{ total: number; byStatus: Record<string, number> }> => {
    const response = await fetch(`${API_BASE_URL}/inquiries/stats`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu. Lutfen tekrar giris yapin.');
      }
      const error = await response.json().catch(() => ({ message: 'Istatistikler yuklenemedi' }));
      throw new Error(error.message || 'Istatistikler yuklenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },
};

/**
 * Featured Categories API endpoints
 */
export const featuredCategoriesApi = {
  // Public: Get all featured categories
  getAll: async (): Promise<FeaturedCategoryItem[]> => {
    return fetchApi('/featured-categories');
  },

  // Admin: Add category to featured list
  add: async (categoryId: string, position?: number): Promise<FeaturedCategoryItem> => {
    const response = await fetch(`${API_BASE_URL}/featured-categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ categoryId, position }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      if (response.status === 409) {
        throw new Error('Bu kategori zaten one cikarilmis');
      }
      const error = await response.json().catch(() => ({ message: 'Kategori one cikarilamadi' }));
      throw new Error(error.message || 'Kategori one cikarilamadi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Admin: Remove from featured list
  remove: async (id: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/featured-categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Kategori one cikarilmaktan kaldirildi' }));
      throw new Error(error.message || 'Islem basarisiz');
    }
    return true;
  },

  // Admin: Remove by category ID
  removeByCategoryId: async (categoryId: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/featured-categories/category/${categoryId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Islem basarisiz' }));
      throw new Error(error.message || 'Islem basarisiz');
    }
    return true;
  },

  // Admin: Update position
  updatePosition: async (id: string, position: number): Promise<FeaturedCategoryItem> => {
    const response = await fetch(`${API_BASE_URL}/featured-categories/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ position }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Siralama guncellenemedi' }));
      throw new Error(error.message || 'Siralama guncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Admin: Bulk update order
  bulkUpdate: async (categoryIds: string[]): Promise<FeaturedCategoryItem[]> => {
    const response = await fetch(`${API_BASE_URL}/featured-categories/bulk`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ categories: categoryIds.map(categoryId => ({ categoryId })) }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Siralama guncellenemedi' }));
      throw new Error(error.message || 'Siralama guncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },
};

/**
 * Featured Products API endpoints
 */
export interface FeaturedProductItem {
  id: string;
  productId: string;
  position: number;
  product: {
    id: string;
    title: string;
    slug: string;
    description?: string;
    image?: {
      id: string;
      url: string;
    };
    categories?: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
  };
  createdAt: string;
}

export const featuredProductsApi = {
  // Public: Get all featured products
  getAll: async (): Promise<FeaturedProductItem[]> => {
    return fetchApi('/featured-products');
  },

  // Admin: Add product to featured list
  add: async (productId: string, position?: number): Promise<FeaturedProductItem> => {
    const response = await fetch(`${API_BASE_URL}/featured-products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ productId, position }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      if (response.status === 409) {
        throw new Error('Bu urun zaten one cikarilmis');
      }
      const error = await response.json().catch(() => ({ message: 'Urun one cikarilamadi' }));
      throw new Error(error.message || 'Urun one cikarilamadi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Admin: Remove from featured list
  remove: async (id: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/featured-products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Islem basarisiz' }));
      throw new Error(error.message || 'Islem basarisiz');
    }
    return true;
  },

  // Admin: Remove by product ID
  removeByProductId: async (productId: string): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/featured-products/product/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Islem basarisiz' }));
      throw new Error(error.message || 'Islem basarisiz');
    }
    return true;
  },

  // Admin: Update position
  updatePosition: async (id: string, position: number): Promise<FeaturedProductItem> => {
    const response = await fetch(`${API_BASE_URL}/featured-products/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ position }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Siralama guncellenemedi' }));
      throw new Error(error.message || 'Siralama guncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },

  // Admin: Bulk update order
  bulkUpdate: async (productIds: string[]): Promise<FeaturedProductItem[]> => {
    const response = await fetch(`${API_BASE_URL}/featured-products/bulk`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ products: productIds.map(productId => ({ productId })) }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Oturum suresi doldu');
      }
      const error = await response.json().catch(() => ({ message: 'Siralama guncellenemedi' }));
      throw new Error(error.message || 'Siralama guncellenemedi');
    }
    const json = await response.json();
    return json.data || json;
  },
};

/**
 * Auth API endpoints
 */
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const url = `${API_BASE_URL}/auth/login`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('E-posta veya sifre hatali');
      }
      if (response.status === 429) {
        throw new Error('Cok fazla deneme. Lutfen biraz bekleyin.');
      }
      if (response.status >= 500) {
        throw new Error('Sunucu hatasi. Lutfen daha sonra tekrar deneyin.');
      }

      const error = await response.json().catch(() => ({ message: 'Giris basarisiz' }));
      throw new Error(error.message || 'Giris basarisiz');
    }

    const json = await response.json();
    // Auth response is also wrapped
    return json.data || json;
  },
};

/**
 * Export all APIs
 */
export const api = {
  categories: categoriesApi,
  products: productsApi,
  inquiries: inquiriesApi,
  featuredCategories: featuredCategoriesApi,
  auth: authApi,
};

export default api;
